import type { CSSProperties } from 'react';
import { LabelSeasonIcon, type LabelForTiresProps } from './LabelForTires';
import { LABEL_PRINTERS, LabelPrinterModel } from './labelPrinter';
import { zebraLabelLayout } from './zebraLabelLayout';

const environment = import.meta.env.MODE;

const ZEBRA = LABEL_PRINTERS[LabelPrinterModel.ZEBRA_ZD230T];

/** The Zebra tire label as it prints, drawn from the same layout as its ZPL. */
export const ZebraLabelPreview = (props: LabelForTiresProps) => {
  const { label, plate, qr, code, make, model, season } = zebraLabelLayout(props, ZEBRA, environment);
  // Dots to the preview's own size: positions in % of the label, fonts in cqh (% of its height).
  const box = (x: number, y: number, width: number, height?: number): CSSProperties => ({
    left: `${(x / label.width) * 100}%`,
    top: `${(y / label.height) * 100}%`,
    width: `${(width / label.width) * 100}%`,
    height: height === undefined ? undefined : `${(height / label.height) * 100}%`
  });
  const fontSize = (dots: number) => `${(dots / label.height) * 100}cqh`;
  const qrPath = qr.modules.flatMap((row, y) => row.map((dark, x) => (dark ? `M${x} ${y}h1v1h-1z` : ''))).join('');

  return (
    <div
      className='[container-type:size] relative w-full bg-white font-bold text-black [font-stretch:condensed]'
      style={{ aspectRatio: `${label.width} / ${label.height}` }}
    >
      <div
        className='absolute flex items-center justify-center rounded-[1cqh] bg-black leading-none text-white'
        style={{ ...box(plate.x, plate.y, plate.width, plate.height), fontSize: fontSize(plate.font) }}
      >
        {plate.text}
      </div>
      <svg
        viewBox={`0 0 ${qr.modules.length} ${qr.modules.length}`}
        shapeRendering='crispEdges'
        role='img'
        aria-label={`QR code ${code.text}`}
        className='absolute'
        style={box(qr.x, qr.y, qr.size, qr.size)}
      >
        <path d={qrPath} fill='currentColor' />
      </svg>
      <p
        className='absolute m-0 text-center leading-none'
        style={{ ...box(code.x, code.y, code.width), fontSize: fontSize(code.font) }}
      >
        {code.text}
      </p>
      <p
        className='absolute m-0 line-clamp-2 leading-none'
        style={{ ...box(make.x, make.y, make.width), fontSize: fontSize(make.font) }}
      >
        {make.text}
      </p>
      <p
        className='absolute m-0 line-clamp-2 leading-none'
        style={{ ...box(model.x, model.y, model.width), fontSize: fontSize(model.font) }}
      >
        {model.text}
      </p>
      {season && (
        <>
          <div className='absolute' style={box(season.iconX, season.iconY, season.iconSize, season.iconSize)}>
            <LabelSeasonIcon season={season.season} className='size-full' />
          </div>
          <p
            className='absolute m-0 text-center leading-none'
            style={{ ...box(season.x, season.y, season.width), fontSize: fontSize(season.font) }}
          >
            {season.text}
          </p>
        </>
      )}
    </div>
  );
};
